import { api, newUuid } from '../client'
import type {
  Address,
  Invitation,
  InviteMemberInput,
  Location,
  MeResponse,
  Member,
  Membership,
  MembershipStatus,
  OccupancyVisibility,
  OpeningHour,
  Organization,
  CustomOrgRole,
  OrgRole,
  OrgRoleListResponse,
  Page,
  UpdateMemberInput,
  Uuid,
} from '../types'

/**
 * Appartenenze dell'account collegato — TEN-06.
 *
 * `GET /me` le espone: `modules/auth/mappers.ts` le serializza da
 * `AuthContext.memberships`, che TEN-04 rilegge a ogni richiesta. Non c'è
 * nessun'altra rotta che elenchi le organizzazioni di un utente — `/organization/*`
 * lavora **dentro** il tenant già risolto e risponderebbe solo su quello — quindi
 * questa è l'unica sorgente del selettore.
 *
 * `bearerToken` esiste per **una** chiamata: quella che decide se una sessione appena
 * ottenuta può entrare nel pannello (`session-store`). Lì il token non è ancora nello
 * store — applicarlo prima di sapere se è ammesso significherebbe far comparire il
 * pannello a chi verrà respinto un istante dopo — quindi va passato a mano.
 *
 * `organization: false`: questa è la chiamata da cui il selettore ricava le
 * organizzazioni valide, e mandarla con una selezione conservata e non più valida
 * la farebbe fallire con `403` — perdendo proprio l'informazione che serve a
 * correggere la selezione. Ha per conseguenza che `current` qui non risente mai
 * dell'header: vale l'organizzazione per cui il token è stato emesso.
 */
export async function listMemberships(
  signal?: AbortSignal,
  options: { bearerToken?: string } = {},
): Promise<Membership[]> {
  const me = await api.get<MeResponse>('/me', {
    organization: false,
    ...(signal ? { signal } : {}),
    ...(options.bearerToken === undefined ? {} : { bearerToken: options.bearerToken }),
  })
  return sanitizeMemberships(me.memberships)
}

/**
 * Scarta le voci inutilizzabili.
 *
 * Una riga senza `organizationId` non può finire nell'header, e una senza nome
 * darebbe una voce di menu vuota su cui nessuno cliccherebbe due volte. Meglio
 * non mostrarla che mostrarne una che non funziona: è la stessa regola con cui
 * `domain/permissions.ts` scarta i permessi sconosciuti — fallire chiuso.
 *
 * Lo stato non è più un campo di `Membership`: `/me` elenca le sole appartenenze
 * **attive** (il server filtra `m.status = 'active'` in SQL), e allargare quel
 * filtro alle sospese non sarebbe una modifica compatibile — il selettore
 * offrirebbe una voce che prende `403` a ogni richiesta — nemmeno se lo schema
 * la accettasse.
 *
 * Lo stato viene comunque scartato se arriva, ed è deliberato: la riga qui sotto
 * non difende da quel cambiamento deliberato, che si vedrebbe in revisione,
 * ma da una `WHERE` che sparisce da `listActiveMemberships` senza che nessuno se
 * ne accorga. Costa un confronto e il guasto da cui protegge è fra i peggiori da
 * diagnosticare: un'organizzazione che compare nel selettore e poi rifiuta ogni
 * richiesta, senza che niente dica perché.
 *
 * Il parametro ammette `undefined` benché il contract dichiari `memberships`
 * obbligatorio: sulla rotta non esiste uno schema di risposta lato server, quindi
 * nulla garantisce la forma a runtime, e un elenco vuoto qui nasconde il selettore
 * invece di far cadere il guscio del pannello su ogni pagina.
 */
function sanitizeMemberships(value: Membership[] | undefined): Membership[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (item): item is Membership =>
      typeof item?.organizationId === 'string' &&
      item.organizationId !== '' &&
      typeof item.organizationName === 'string' &&
      item.organizationName !== '' &&
      // Letto come proprietà a runtime e non dal tipo, perché nel tipo non c'è:
      // se un giorno riapparisse, deve essere `active`. L'assenza è il caso
      // normale e passa.
      (item as { status?: unknown }).status !== 'suspended' &&
      (item as { status?: unknown }).status !== 'invited',
  )
}

// ── operatori (OPS-05) ────────────────────────────────────────────────────────
//
// Nessuna di queste rotte accetta un `organizationId`: l'ambito viene dal
// contesto della richiesta, cioè da `X-Organization-Id` (TEN-04). È una regola
// del backend, non una semplificazione — senza, un permesso `member:write`
// diventerebbe «scrivi sui membri di *qualunque* organizzazione».

export interface ListMembersParams {
  role?: OrgRole
  status?: MembershipStatus
  limit?: number
  cursor?: string
}

/**
 * `GET /organization/members`.
 *
 * `meta.total` non arriva neanche qui: il servizio restituisce solo
 * `nextCursor`. `CursorPager` lo gestisce già mostrando le righe della pagina
 * invece di un totale inventato.
 */
export function listMembers(
  params: ListMembersParams = {},
  signal?: AbortSignal,
): Promise<Page<Member>> {
  return api.get<Page<Member>>('/organization/members', {
    query: {
      role: params.role,
      status: params.status,
      limit: params.limit ?? 50,
      cursor: params.cursor,
    },
    ...(signal ? { signal } : {}),
  })
}

/**
 * `PATCH /organization/members/{id}` — `204`, nessun corpo di risposta.
 *
 * Le regole che possono farla fallire stanno tutte lato server e vanno mostrate,
 * non riscritte: non si conferisce un ruolo superiore al proprio
 * (`403 insufficient_permissions`), non si sospende sé stessi né si retrocede
 * l'ultimo titolare (`409 cannot_modify_self`).
 */
export function updateMember(memberId: Uuid, body: UpdateMemberInput): Promise<void> {
  return api.patch<void>(`/organization/members/${encodeURIComponent(memberId)}`, body)
}

/** `DELETE /organization/members/{id}`: toglie l'appartenenza, **non** l'account. */
export function removeMember(memberId: Uuid): Promise<void> {
  return api.delete(`/organization/members/${encodeURIComponent(memberId)}`)
}

// ── inviti ────────────────────────────────────────────────────────────────────

/**
 * `GET /organization/invitations`.
 *
 * Array nudo e non `Page<T>`: il servizio restituisce solo gli inviti **aperti**,
 * che sono l'elenco utile e non hanno bisogno di paginazione.
 */
export function listInvitations(signal?: AbortSignal): Promise<Invitation[]> {
  return api.get<Invitation[]>('/organization/invitations', {
    ...(signal ? { signal } : {}),
  })
}

/**
 * `POST /organization/invitations` — `201`.
 *
 * `Idempotency-Key` perché un timeout di rete su questa chiamata è ambiguo, e
 * riprovare senza chiave manderebbe una seconda email allo stesso indirizzo. Il
 * token dell'invito **non** esce nella risposta: viaggia solo nell'email, così
 * chi ha `member:invite` non può fabbricare un link e usarlo altrove.
 */
export function inviteMember(body: InviteMemberInput): Promise<Invitation> {
  return api.post<Invitation>('/organization/invitations', body, {
    idempotencyKey: newUuid(),
  })
}

/**
 * `POST /invitations/accept` — **fuori** dal perimetro `/organization/*`.
 *
 * Non è una simmetria mancata: chi accetta non è ancora membro di nulla, quindi
 * chiedergli un permesso dentro l'organizzazione sarebbe circolare. Serve solo un
 * utente autenticato, e l'organizzazione la decide l'invito — `X-Organization-Id`
 * qui non ha effetto, e il client non lo manda.
 *
 * [bearerToken] c'è perché questa chiamata avviene nel momento più insolito del
 * pannello: **fra l'autenticazione e l'applicazione della sessione**. Chi accetta un
 * primo invito non ha ancora nessuna appartenenza, quindi `applyLoginSession` lo
 * respingerebbe — è l'invito stesso a renderlo una sessione valida per il pannello.
 * Vedi `acceptPendingInvitation` in `session-store.ts`.
 */
export function acceptInvitation(
  token: string,
  options?: { bearerToken?: string },
): Promise<{ organizationId: Uuid; role: string }> {
  return api.post<{ organizationId: Uuid; role: string }>(
    '/invitations/accept',
    { token },
    {
      organization: false,
      ...(options?.bearerToken ? { bearerToken: options.bearerToken } : {}),
    },
  )
}

/** `POST /organization/invitations/{id}/revoke` — `204`. */
export function revokeInvitation(invitationId: Uuid): Promise<void> {
  return api.post<void>(
    `/organization/invitations/${encodeURIComponent(invitationId)}/revoke`,
    undefined,
    { idempotencyKey: newUuid() },
  )
}

// ── anagrafica dell'organizzazione e sedi (contract `1.0.0-draft.27`) ─────────
//
// Anche qui l'organizzazione non si nomina: è quella risolta dalla richiesta,
// come per operatori e inviti. `GET /organization` chiede `organization:read`,
// la scrittura `organization:update` — che nei ruoli predefiniti ha il solo
// titolare, e che pretende il secondo fattore: sono i dati con cui si emette una
// fattura a nome di qualcuno.

/** `GET /organization`, con l'`ETag` da rimandare in `If-Match`. */
export function getOrganization(signal?: AbortSignal) {
  return api.getTagged<Organization>('/organization', { ...(signal ? { signal } : {}) })
}

/**
 * Ciò che si può scrivere, che è meno di ciò che si legge.
 *
 * Fuori restano `plan`, `subscriptionStatus` e `disabled`: appartengono
 * all'abbonamento alla piattaforma. Fuori resta anche il verso `gym` → `personal`,
 * che il server rifiuta con `409` — a quel punto ci sono iscritti, vendite e
 * accessi attaccati.
 */
export interface OrganizationPatch {
  name?: string
  legalName?: string | null
  kind?: 'gym'
  slug?: string | null
  vatNumber?: string | null
  taxCode?: string | null
  address?: Partial<Address>
  email?: string | null
  phone?: string | null
  website?: string | null
  logoUrl?: string | null
}

/** `PATCH /organization`. `409` su slug già preso, `412` su `ETag` non combaciante. */
export function updateOrganization(body: OrganizationPatch, etag?: string | null) {
  return api.patch<Organization>('/organization', body, { ifMatch: etag })
}

/**
 * `GET /organization/locations` — array nudo sotto `data`, senza paginazione.
 *
 * Le sedi di una palestra sono una o dieci: un cursore su un elenco che sta in
 * uno schermo aggiungerebbe uno stato da gestire e niente altro.
 */
export function listLocations(archived = false, signal?: AbortSignal): Promise<Location[]> {
  return api
    .get<{ data: Location[] }>('/organization/locations', {
      query: { archived },
      ...(signal ? { signal } : {}),
    })
    .then((response) => response.data)
}

export interface LocationInput {
  name: string
  address?: Partial<Address>
  phone?: string
  email?: string
  timezone?: string
  capacity?: number
  openingHours?: OpeningHour[]
  /**
   * ACC-07, da 15 a 720 minuti. Qui `null` è ammesso, a differenza degli altri campi della
   * creazione, e non vuol dire «non dichiarato»: vuol dire **mai**. Ometterlo dà 90, che è
   * un'altra scelta — per questo il pannello lo manda sempre.
   */
  presumedExitAfterMinutes?: number | null
  occupancyVisibility?: OccupancyVisibility
}

export type LocationPatch = Partial<Omit<LocationInput, 'phone' | 'email' | 'capacity'>> & {
  phone?: string | null
  email?: string | null
  capacity?: number | null
}

export function getLocation(locationId: Uuid, signal?: AbortSignal) {
  return api.getTagged<Location>(`/organization/locations/${encodeURIComponent(locationId)}`, {
    ...(signal ? { signal } : {}),
  })
}

export function createLocation(body: LocationInput): Promise<Location> {
  return api.post<Location>('/organization/locations', body)
}

export function updateLocation(
  locationId: Uuid,
  body: LocationPatch,
  etag?: string | null,
): Promise<Location> {
  return api.patch<Location>(`/organization/locations/${encodeURIComponent(locationId)}`, body, {
    ifMatch: etag,
  })
}

/**
 * `DELETE /organization/locations/{id}` — **archivia**, non cancella.
 *
 * `409 location_in_use` finché esistono iscrizioni attive legate a quella sola
 * sede: vanno spostate prima, ed è una decisione di chi gestisce, non del server.
 */
export function archiveLocation(locationId: Uuid): Promise<void> {
  return api.delete(`/organization/locations/${encodeURIComponent(locationId)}`)
}

// ── i ruoli personalizzati (OPS-03) ──────────────────────────────────────────
//
// ⚠️ Queste quattro rotte erano irraggiungibili, e il motivo per cui lo sono rimaste è la nota
// più istruttiva dell'audit del 20 agosto. `operatori.index.tsx` portava questo commento:
//
//   «`/organization/roles` **esiste** lato backend, ma non è descritta nel contract — e il
//    contract viene prima. Finché non c'è, i ruoli sono quelli predefiniti.»
//
// Era vero quando è stato scritto, ed è **smesso di esserlo il 13 agosto**, quando le dodici
// rotte arretrate sono entrate nel contract (`382ef49`). Da allora il blocco non c'era più e
// nessuno è tornato a togliere l'avviso: la nota che descriveva l'attesa è sopravvissuta
// all'attesa. È la stessa malattia che §1 del piano dichiara — la documentazione va indietro da
// sola —, con la differenza che qui viveva nel **codice**, dove nessuna rilettura del piano
// poteva trovarla.

export function listOrganizationRoles(signal?: AbortSignal): Promise<OrgRoleListResponse> {
  return api.get<OrgRoleListResponse>('/organization/roles', { ...(signal ? { signal } : {}) })
}

export function createOrganizationRole(input: {
  key: string
  label: string
  basedOn?: OrgRole
  permissions?: string[]
}): Promise<CustomOrgRole> {
  return api.post<CustomOrgRole>('/organization/roles', input)
}

/** `key` e `basedOn` non si modificano: si ottengono eliminando e ricreando, che è tracciato. */
export function updateOrganizationRole(
  roleId: Uuid,
  input: { label?: string; permissions?: string[] },
): Promise<CustomOrgRole> {
  return api.patch<CustomOrgRole>(`/organization/roles/${encodeURIComponent(roleId)}`, input)
}

export function deleteOrganizationRole(roleId: Uuid): Promise<void> {
  return api.delete(`/organization/roles/${encodeURIComponent(roleId)}`)
}
